/** The app's icon set — Material Symbols (outlined, weight 400).
 *
 *  Source: https://fonts.google.com/icons, Apache License 2.0.
 *
 *  Exports every name the app previously imported from `lucide-react`, with the
 *  same call signature, so a file migrates by changing only its import line.
 *  `LucideIcon` is re-exported as a type alias for the same reason — it is used
 *  as a prop type in several places.
 *
 *  Why the paths are inlined rather than loaded as the icon font: the app makes
 *  no third-party font request anywhere, and an icon font renders as tofu until
 *  it loads, which would flash on every screen. Inlined, the whole set is ~80KB
 *  of path data — less than the ~309 separate Lucide icon modules it replaces.
 *
 *  These are FILLED shapes on a 960 grid. `strokeWidth` is accepted and ignored:
 *  Material bakes weight into the geometry, so there is no stroke to set. Call
 *  sites that pass it are harmless and need no edit.
 *
 *  Several Lucide names collapse onto one symbol (Edit/Edit2/Edit3/Pen/Pencil
 *  are all `edit`), which is deliberate — Material draws one glyph where Lucide
 *  drew variants.
 */

import React from 'react';
import {d_format_color_fill, d_home_fill, d_calendar_month_fill, d_chat_fill, d_mail_fill, d_contact_page_fill, d_credit_card_fill, d_send_fill, d_view_kanban_fill, d_payments_fill, d_shopping_cart_fill, d_account_balance_wallet_fill, d_library_books_fill, d_menu_book_fill, d_assignment_turned_in_fill, d_edit_note_fill, d_local_shipping_fill, d_work_fill} from './iconPaths';
import {
  d_account_balance,
  d_account_balance_wallet,
  d_account_circle,
  d_account_tree,
  d_add,
  d_add_comment,
  d_air,
  d_all_inclusive,
  d_alternate_email,
  d_apartment,
  d_archive,
  d_arrow_back,
  d_arrow_downward,
  d_arrow_forward,
  d_arrow_selector_tool,
  d_arrow_upward,
  d_assignment,
  d_assignment_turned_in,
  d_attach_file,
  d_audio_file,
  d_auto_awesome,
  d_auto_fix_high,
  d_balance,
  d_bar_chart,
  d_block,
  d_bolt,
  d_book,
  d_bookmark,
  d_bookmark_add,
  d_bookmark_added,
  d_brightness_low,
  d_bug_report,
  d_build,
  d_calculate,
  d_calendar_month,
  d_call,
  d_call_end,
  d_campaign,
  d_cancel,
  d_chat,
  d_chat_bubble,
  d_check,
  d_check_box,
  d_check_circle,
  d_checklist,
  d_close,
  d_close_fullscreen,
  d_cloud,
  d_cloud_download,
  d_cloud_upload,
  d_co_present,
  d_code,
  d_comment,
  d_contact_page,
  d_contacts,
  d_content_copy,
  d_content_paste,
  d_contrast,
  d_create_new_folder,
  d_credit_card,
  d_crop_square,
  d_currency_pound,
  d_dashboard,
  d_database,
  d_delete,
  d_description,
  d_desktop_windows,
  d_dns,
  d_do_not_disturb_on,
  d_domain,
  d_done_all,
  d_download,
  d_draft,
  d_drafts,
  d_drag_handle,
  d_drag_indicator,
  d_draw,
  d_drive_file_move,
  d_edit,
  d_edit_calendar,
  d_edit_document,
  d_edit_note,
  d_edit_square,
  d_engineering,
  d_error,
  d_event_available,
  d_event_busy,
  d_event_upcoming,
  d_file_copy,
  d_file_download,
  d_file_upload,
  d_filter_alt,
  d_find_in_page,
  d_flag,
  d_folder,
  d_folder_delete,
  d_folder_open,
  d_folder_special,
  d_folder_zip,
  d_format_align_left,
  d_forward,
  d_gavel,
  d_gpp_bad,
  d_gpp_maybe,
  d_grid_on,
  d_grid_view,
  d_group,
  d_groups,
  d_handshake,
  d_hardware,
  d_help,
  d_hide_image,
  d_history,
  d_home,
  d_how_to_reg,
  d_image,
  d_inbox,
  d_info,
  d_ink_eraser,
  d_ink_highlighter,
  d_inventory_2,
  d_keep_off,
  d_key,
  d_keyboard,
  d_keyboard_arrow_down,
  d_keyboard_arrow_left,
  d_keyboard_arrow_right,
  d_keyboard_arrow_up,
  d_keyboard_command_key,
  d_keyboard_double_arrow_left,
  d_keyboard_double_arrow_right,
  d_language,
  d_layers,
  d_light_mode,
  d_lightbulb,
  d_link,
  d_link_off,
  d_list,
  d_local_cafe,
  d_local_fire_department,
  d_local_library,
  d_library_books,
  d_local_shipping,
  d_location_on,
  d_lock,
  d_lock_open,
  d_login,
  d_logout,
  d_mail,
  d_manage_accounts,
  d_map,
  d_menu_book,
  d_merge,
  d_mic,
  d_mic_off,
  d_monitoring,
  d_more_horiz,
  d_more_vert,
  d_my_location,
  d_navigation,
  d_nights_stay,
  d_north_east,
  d_note_add,
  d_notifications,
  d_notifications_off,
  d_open_in_full,
  d_open_in_new,
  d_open_with,
  d_palette,
  d_pause,
  d_pause_circle,
  d_payments,
  d_percent,
  d_person,
  d_person_add,
  d_person_off,
  d_person_remove,
  d_phone_in_talk,
  d_photo_camera,
  d_picture_in_picture,
  d_pie_chart,
  d_play_arrow,
  d_play_circle,
  d_playlist_add,
  d_print,
  d_progress_activity,
  d_public,
  d_push_pin,
  d_qr_code,
  d_quiz,
  d_radio_button_checked,
  d_radio_button_unchecked,
  d_rainy,
  d_receipt,
  d_receipt_long,
  d_redo,
  d_refresh,
  d_remove,
  d_repeat,
  d_reply,
  d_reply_all,
  d_report,
  d_rocket_launch,
  d_rotate_left,
  d_save,
  d_schedule,
  d_search,
  d_search_off,
  d_send,
  d_settings,
  d_share,
  d_shield,
  d_shopping_cart,
  d_show_chart,
  d_smart_toy,
  d_smartphone,
  d_sort,
  d_south_east,
  d_space_dashboard,
  d_speed,
  d_star,
  d_sticky_note_2,
  d_storage,
  d_storefront,
  d_straighten,
  d_subdirectory_arrow_left,
  d_support,
  d_swap_horiz,
  d_swap_vert,
  d_table_chart,
  d_table_rows,
  d_table_view,
  d_tag,
  d_target,
  d_task,
  d_text_fields,
  d_thumb_down,
  d_thumb_up,
  d_thunderstorm,
  d_timer,
  d_trending_down,
  d_trending_up,
  d_tune,
  d_unarchive,
  d_undo,
  d_unfold_less,
  d_unfold_more,
  d_upload,
  d_verified_user,
  d_video_file,
  d_videocam,
  d_view_column,
  d_view_kanban,
  d_view_list,
  d_visibility,
  d_visibility_off,
  d_volume_off,
  d_volume_up,
  d_wallet,
  d_warning,
  d_water_drop,
  d_weather_snowy,
  d_wifi,
  d_wifi_off,
  d_work,
  d_workspace_premium,
  d_zoom_in,
  d_zoom_out,
  d_arrow_range,
  d_frame_inspect,
  d_highlight_alt,
  d_view_in_ar,
  d_document_takeoff,
} from './iconPaths';

export interface IconProps extends Omit<React.SVGProps<SVGSVGElement>, 'ref'> {
  /** Matches Lucide: sets both width and height. A className like `w-4 h-4`
   *  still wins, since CSS beats the presentation attribute. */
  size?: number | string;
  /** Accepted for source compatibility with Lucide; Material has no stroke. */
  strokeWidth?: number | string;
}

/** Source-compatible with Lucide's exported type, which the app uses to type
 *  icon props (e.g. `icon: LucideIcon`). */
export type LucideIcon = React.FC<IconProps> & {
  /** The glyph's path data and grid, so React Native code can draw the same icon. */
  iconPath?: string;
  iconViewBox?: string;
};

function make(d: string, displayName: string, viewBox = '0 -960 960 960'): LucideIcon {
  const Icon: LucideIcon = ({ size = 24, strokeWidth: _ignored, ...rest }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill="currentColor"
      aria-hidden={rest['aria-label'] ? undefined : true}
      {...rest}
    >
      <path d={d} />
    </svg>
  );
  Icon.displayName = displayName;
  Icon.iconPath = d;
  Icon.iconViewBox = viewBox;
  return Icon;
}

// ── measurement + drawing glyphs, which Lucide had no equivalent for ───────
// Exported under their Material names because there is no Lucide name to
// borrow. The takeoff registry aliases them to what the tool is called.
const d_deployed_code = 'M440-183v-274L200-596v274l240 139Zm80 0 240-139v-274L520-457v274Zm-40-343 237-137-237-137-237 137 237 137ZM160-252q-19-11-29.5-29T120-321v-318q0-22 10.5-40t29.5-29l280-161q19-11 40-11t40 11l280 161q19 11 29.5 29t10.5 40v318q0 22-10.5 40T800-252L520-91q-19 11-40 11t-40-11L160-252Zm320-228Z';
const d_pan_tool = 'M402-40q-30 0-56-13.5T303-92L48-465l24-23q19-19 45-22t47 12l116 81v-383q0-17 11.5-28.5T320-840q17 0 28.5 11.5T360-800v537L212-367l157 229q5 8 14 13t19 5h278q33 0 56.5-23.5T760-200v-560q0-17 11.5-28.5T800-800q17 0 28.5 11.5T840-760v560q0 66-47 113T680-40H402Zm38-440v-400q0-17 11.5-28.5T480-920q17 0 28.5 11.5T520-880v400h-80Zm160 0v-360q0-17 11.5-28.5T640-880q17 0 28.5 11.5T680-840v360h-80ZM486-300Z';
const d_polyline = 'M600-80v-100L320-320H120v-240h172l108-124v-196h240v240H468L360-516v126l240 120v-50h240v240H600ZM480-720h80v-80h-80v80ZM200-400h80v-80h-80v80Zm480 240h80v-80h-80v80ZM520-760ZM240-440Zm480 240Z';
const d_scatter_plot = 'M567-167q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q760-247 760-280t-23.5-56.5Q713-360 680-360t-56.5 23.5Q600-313 600-280t23.5 56.5Q647-200 680-200t56.5-23.5ZM167-287q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q360-367 360-400t-23.5-56.5Q313-480 280-480t-56.5 23.5Q200-433 200-400t23.5 56.5Q247-320 280-320t56.5-23.5ZM327-607q-47-47-47-113t47-113q47-47 113-47t113 47q47 47 47 113t-47 113q-47 47-113 47t-113-47Zm169.5-56.5Q520-687 520-720t-23.5-56.5Q473-800 440-800t-56.5 23.5Q360-753 360-720t23.5 56.5Q407-640 440-640t56.5-23.5ZM680-280ZM280-400Zm160-320Z';
const d_square_foot = 'M208-120q-37 0-62.5-25.5T120-208v-548q0-29 27-40.5t47 8.5l90 90-54 54 28 28 54-54 104 104-54 54 28 28 54-54 104 104-54 54 28 28 54-54 104 104-54 54 28 28 54-54 80 80q20 20 8.5 47T756-120H208Zm32-120h332L240-572v332Z';

// ── message formatting glyphs (Material format_* and mood) ────────────────
const d_format_list_numbered = 'M120-80v-60h100v-30h-60v-60h60v-30H120v-60h120q17 0 28.5 11.5T280-280v40q0 17-11.5 28.5T240-200q17 0 28.5 11.5T280-160v40q0 17-11.5 28.5T240-80H120Zm0-280v-110q0-17 11.5-28.5T160-510h60v-30H120v-60h120q17 0 28.5 11.5T280-560v70q0 17-11.5 28.5T240-450h-60v30h100v60H120Zm60-280v-180h-60v-60h120v240h-60Zm180 440v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360Z';
const d_format_strikethrough = 'M80-400v-80h800v80H80Zm340-160v-120H200v-120h560v120H540v120H420Zm0 400v-160h120v160H420Z';
const d_format_quote = 'm228-240 92-160q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 23-5.5 42.5T458-480L320-240h-92Zm360 0 92-160q-66 0-113-47t-47-113q0-66 47-113t113-47q66 0 113 47t47 113q0 23-5.5 42.5T818-480L680-240h-92ZM362.5-517.5Q380-535 380-560t-17.5-42.5Q345-620 320-620t-42.5 17.5Q260-585 260-560t17.5 42.5Q295-500 320-500t42.5-17.5Zm360 0Q740-535 740-560t-17.5-42.5Q705-620 680-620t-42.5 17.5Q620-585 620-560t17.5 42.5Q655-500 680-500t42.5-17.5ZM680-560Zm-360 0Z';
const d_mood = 'M620-520q25 0 42.5-17.5T680-580q0-25-17.5-42.5T620-640q-25 0-42.5 17.5T560-580q0 25 17.5 42.5T620-520Zm-280 0q25 0 42.5-17.5T400-580q0-25-17.5-42.5T340-640q-25 0-42.5 17.5T280-580q0 25 17.5 42.5T340-520Zm263.5 221.5Q659-337 684-400H276q25 63 80.5 101.5T480-260q68 0 123.5-38.5ZM324-111.5Q251-143 197-197t-85.5-127Q80-397 80-480t31.5-156Q143-709 197-763t127-85.5Q397-880 480-880t156 31.5Q709-817 763-763t85.5 127Q880-563 880-480t-31.5 156Q817-251 763-197t-127 85.5Q563-80 480-80t-156-31.5ZM480-480Zm227 227q93-93 93-227t-93-227q-93-93-227-93t-227 93q-93 93-93 227t93 227q93 93 227 93t227-93Z';

const d_add_reaction = 'M480-480Zm0 400q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q43 0 83 8.5t77 24.5v90q-35-20-75.5-31.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160q133 0 226.5-93.5T800-480q0-32-6.5-62T776-600h86q9 29 13.5 58.5T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm320-600v-80h-80v-80h80v-80h80v80h80v80h-80v80h-80ZM620-520q25 0 42.5-17.5T680-580q0-25-17.5-42.5T620-640q-25 0-42.5 17.5T560-580q0 25 17.5 42.5T620-520Zm-280 0q25 0 42.5-17.5T400-580q0-25-17.5-42.5T340-640q-25 0-42.5 17.5T280-580q0 25 17.5 42.5T340-520Zm140 260q68 0 123.5-38.5T684-400H276q25 63 80.5 101.5T480-260Z';

// ── exports, named as Lucide named them ────────────────────────────────────
export const Activity = make(d_monitoring, 'Activity');
export const SmilePlus = make(d_add_reaction, 'SmilePlus');
export const Smile = make(d_mood, 'Smile');
export const ListOrdered = make(d_format_list_numbered, 'ListOrdered');
export const Strikethrough = make(d_format_strikethrough, 'Strikethrough');
export const Quote = make(d_format_quote, 'Quote');
export const AlertCircle = make(d_error, 'AlertCircle');
export const AlertOctagon = make(d_report, 'AlertOctagon');
export const AlertTriangle = make(d_warning, 'AlertTriangle');
export const AlignLeft = make(d_format_align_left, 'AlignLeft');
export const Archive = make(d_archive, 'Archive');
export const ArchiveRestore = make(d_unarchive, 'ArchiveRestore');
export const ArrowDown = make(d_arrow_downward, 'ArrowDown');
export const ArrowDownRight = make(d_south_east, 'ArrowDownRight');
export const ArrowDownUp = make(d_swap_vert, 'ArrowDownUp');
export const ArrowLeft = make(d_arrow_back, 'ArrowLeft');
export const ArrowRight = make(d_arrow_forward, 'ArrowRight');
export const ArrowRightLeft = make(d_swap_horiz, 'ArrowRightLeft');
export const ArrowUp = make(d_arrow_upward, 'ArrowUp');
export const ArrowUpDown = make(d_swap_vert, 'ArrowUpDown');
export const ArrowUpRight = make(d_north_east, 'ArrowUpRight');
export const AtSign = make(d_alternate_email, 'AtSign');
export const Ban = make(d_block, 'Ban');
export const Banknote = make(d_payments, 'Banknote');
export const BarChart = make(d_bar_chart, 'BarChart');
export const BarChart2 = make(d_bar_chart, 'BarChart2');
export const BarChart3 = make(d_bar_chart, 'BarChart3');
export const Bell = make(d_notifications, 'Bell');
export const BellOff = make(d_notifications_off, 'BellOff');
export const BookOpen = make(d_menu_book, 'BookOpen');
export const BookText = make(d_book, 'BookText');
export const BookUser = make(d_contact_page, 'BookUser');
export const Bookmark = make(d_bookmark, 'Bookmark');
export const BookmarkCheck = make(d_bookmark_added, 'BookmarkCheck');
export const BookmarkPlus = make(d_bookmark_add, 'BookmarkPlus');
export const Bot = make(d_smart_toy, 'Bot');
export const Briefcase = make(d_work, 'Briefcase');
export const Bug = make(d_bug_report, 'Bug');
export const Building = make(d_apartment, 'Building');
export const Building2 = make(d_domain, 'Building2');
export const Calculator = make(d_calculate, 'Calculator');
export const CalculatorDevice = make('M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm0 2v16h10V4H7Zm2 2h6v3H9V6Zm0 5h2v2H9v-2Zm4 0h2v2h-2v-2Zm-4 4h2v3H9v-3Zm4 0h2v3h-2v-3Z', 'CalculatorDevice', '0 0 24 24');
export const Calendar = make(d_calendar_month, 'Calendar');
export const CalendarCheck = make(d_event_available, 'CalendarCheck');
export const CalendarClock = make(d_event_upcoming, 'CalendarClock');
export const CalendarDays = make(d_calendar_month, 'CalendarDays');
export const CalendarIcon = make(d_calendar_month, 'CalendarIcon');
export const CalendarOff = make(d_event_busy, 'CalendarOff');
export const CalendarPlus = make(d_edit_calendar, 'CalendarPlus');
export const CalendarX2 = make(d_event_busy, 'CalendarX2');
export const Camera = make(d_photo_camera, 'Camera');
export const Check = make(d_check, 'Check');
export const CheckCheck = make(d_done_all, 'CheckCheck');
export const CheckCircle = make(d_check_circle, 'CheckCircle');
export const CheckCircle2 = make(d_check_circle, 'CheckCircle2');
export const CheckIcon = make(d_check, 'CheckIcon');
export const CheckSquare = make(d_check_box, 'CheckSquare');
export const ChevronDown = make(d_keyboard_arrow_down, 'ChevronDown');
export const ChevronDownIcon = make(d_keyboard_arrow_down, 'ChevronDownIcon');
export const ChevronLeft = make(d_keyboard_arrow_left, 'ChevronLeft');
export const ChevronLeftIcon = make(d_keyboard_arrow_left, 'ChevronLeftIcon');
export const ChevronRight = make(d_keyboard_arrow_right, 'ChevronRight');
export const ChevronRightIcon = make(d_keyboard_arrow_right, 'ChevronRightIcon');
export const ChevronUp = make(d_keyboard_arrow_up, 'ChevronUp');
export const ChevronUpIcon = make(d_keyboard_arrow_up, 'ChevronUpIcon');
export const ChevronsDownUp = make(d_unfold_less, 'ChevronsDownUp');
export const ChevronsLeft = make(d_keyboard_double_arrow_left, 'ChevronsLeft');
export const ChevronsRight = make(d_keyboard_double_arrow_right, 'ChevronsRight');
export const ChevronsUpDown = make(d_unfold_more, 'ChevronsUpDown');
export const Circle = make(d_radio_button_unchecked, 'Circle');
export const CircleAlert = make(d_error, 'CircleAlert');
export const CircleCheck = make(d_check_circle, 'CircleCheck');
export const CircleDot = make(d_radio_button_checked, 'CircleDot');
export const CircleHelp = make(d_help, 'CircleHelp');
export const CircleIcon = make(d_radio_button_unchecked, 'CircleIcon');
export const CircleUser = make(d_account_circle, 'CircleUser');
export const Clipboard = make(d_content_paste, 'Clipboard');
export const ClipboardCheck = make(d_assignment_turned_in, 'ClipboardCheck');
export const ClipboardList = make(d_assignment, 'ClipboardList');
export const Clock = make(d_schedule, 'Clock');
export const Clock3 = make(d_schedule, 'Clock3');
export const Cloud = make(d_cloud, 'Cloud');
export const CloudDownload = make(d_cloud_download, 'CloudDownload');
export const CloudDrizzle = make(d_rainy, 'CloudDrizzle');
export const CloudLightning = make(d_thunderstorm, 'CloudLightning');
export const CloudMoon = make(d_nights_stay, 'CloudMoon');
export const CloudRain = make(d_rainy, 'CloudRain');
export const CloudSnow = make(d_weather_snowy, 'CloudSnow');
export const CloudUpload = make(d_cloud_upload, 'CloudUpload');
export const Coffee = make(d_local_cafe, 'Coffee');
export const Columns = make(d_view_column, 'Columns');
export const Command = make(d_keyboard_command_key, 'Command');
export const Contact = make(d_contacts, 'Contact');
export const Contrast = make(d_contrast, 'Contrast');
export const Copy = make(d_content_copy, 'Copy');
export const CornerDownLeft = make(d_subdirectory_arrow_left, 'CornerDownLeft');
export const CreditCard = make(d_credit_card, 'CreditCard');
export const Crosshair = make(d_my_location, 'Crosshair');
export const Crown = make(d_workspace_premium, 'Crown');
export const Database = make(d_database, 'Database');
export const Download = make(d_download, 'Download');
export const Droplets = make(d_water_drop, 'Droplets');
export const Edit = make(d_edit, 'Edit');
export const Edit2 = make(d_edit, 'Edit2');
export const Edit3 = make(d_edit, 'Edit3');
export const Eraser = make(d_ink_eraser, 'Eraser');
export const ExternalLink = make(d_open_in_new, 'ExternalLink');
export const Eye = make(d_visibility, 'Eye');
export const EyeOff = make(d_visibility_off, 'EyeOff');
export const File = make(d_draft, 'File');
export const FileArchive = make(d_folder_zip, 'FileArchive');
export const FileAudio = make(d_audio_file, 'FileAudio');
export const FileCheck = make(d_task, 'FileCheck');
export const FileCode = make(d_code, 'FileCode');
export const FileCode2 = make(d_code, 'FileCode2');
export const FileDown = make(d_file_download, 'FileDown');
export const FileEdit = make(d_edit_document, 'FileEdit');
export const FileImage = make(d_image, 'FileImage');
export const FilePenLine = make(d_edit_document, 'FilePenLine');
export const FilePlus = make(d_note_add, 'FilePlus');
export const FilePlus2 = make(d_note_add, 'FilePlus2');
export const FileQuestion = make(d_quiz, 'FileQuestion');
export const FileSearch = make(d_find_in_page, 'FileSearch');
export const FileSignature = make(d_draw, 'FileSignature');
export const FileSpreadsheet = make(d_table_view, 'FileSpreadsheet');
export const FileStack = make(d_file_copy, 'FileStack');
export const FileText = make(d_description, 'FileText');
export const FileUp = make(d_file_upload, 'FileUp');
export const FileVideo = make(d_video_file, 'FileVideo');
export const FileWarning = make(d_report, 'FileWarning');
export const Files = make(d_file_copy, 'Files');
export const Filter = make(d_filter_alt, 'Filter');
export const Flag = make(d_flag, 'Flag');
export const Flame = make(d_local_fire_department, 'Flame');
export const Folder = make(d_folder, 'Folder');
export const FolderInput = make(d_drive_file_move, 'FolderInput');
export const FolderKanban = make(d_folder_special, 'FolderKanban');
export const FolderOpen = make(d_folder_open, 'FolderOpen');
export const FolderPlus = make(d_create_new_folder, 'FolderPlus');
export const FolderX = make(d_folder_delete, 'FolderX');
export const Forward = make(d_forward, 'Forward');
export const Gauge = make(d_speed, 'Gauge');
export const Gavel = make(d_gavel, 'Gavel');
export const GitPullRequest = make(d_merge, 'GitPullRequest');
export const Globe = make(d_language, 'Globe');
export const Globe2 = make(d_public, 'Globe2');
export const Grid3x3 = make(d_grid_on, 'Grid3x3');
export const GripHorizontal = make(d_drag_handle, 'GripHorizontal');
export const GripVertical = make(d_drag_indicator, 'GripVertical');
export const GripVerticalIcon = make(d_drag_indicator, 'GripVerticalIcon');
export const Hammer = make(d_hardware, 'Hammer');
export const Handshake = make(d_handshake, 'Handshake');
export const HardHat = make(d_engineering, 'HardHat');
export const Hash = make(d_tag, 'Hash');
export const HelpCircle = make(d_help, 'HelpCircle');
export const Highlighter = make(d_ink_highlighter, 'Highlighter');
export const History = make(d_history, 'History');
export const Home = make(d_home, 'Home');
export const Image = make(d_image, 'Image');
export const ImageIcon = make(d_image, 'ImageIcon');
export const ImageOff = make(d_hide_image, 'ImageOff');
export const Inbox = make(d_inbox, 'Inbox');
export const Infinity = make(d_all_inclusive, 'Infinity');
export const Info = make(d_info, 'Info');
export const KanbanSquare = make(d_view_kanban, 'KanbanSquare');
export const Key = make(d_key, 'Key');
export const KeyRound = make(d_key, 'KeyRound');
export const Keyboard = make(d_keyboard, 'Keyboard');
export const Landmark = make(d_account_balance, 'Landmark');
export const Layers = make(d_layers, 'Layers');
export const LayoutDashboard = make(d_dashboard, 'LayoutDashboard');
export const LayoutGrid = make(d_grid_view, 'LayoutGrid');
export const LayoutList = make(d_view_list, 'LayoutList');
export const LayoutTemplate = make(d_space_dashboard, 'LayoutTemplate');
export const Library = make(d_local_library, 'Library');
export const LibraryBooks = make(d_library_books, 'LibraryBooks');
export const LifeBuoy = make(d_support, 'LifeBuoy');
export const Lightbulb = make(d_lightbulb, 'Lightbulb');
export const LineChart = make(d_show_chart, 'LineChart');
export const Link = make(d_link, 'Link');
export const Link2 = make(d_link, 'Link2');
export const LinkIcon = make(d_link, 'LinkIcon');
export const List = make(d_list, 'List');
export const ListChecks = make(d_checklist, 'ListChecks');
export const ListPlus = make(d_playlist_add, 'ListPlus');
export const ListTodo = make(d_checklist, 'ListTodo');
export const Loader = make(d_progress_activity, 'Loader');
export const Loader2 = make(d_progress_activity, 'Loader2');
export const Lock = make(d_lock, 'Lock');
export const LogIn = make(d_login, 'LogIn');
export const LogOut = make(d_logout, 'LogOut');
export const Mail = make(d_mail, 'Mail');
export const MailOpen = make(d_drafts, 'MailOpen');
export const Map = make(d_map, 'Map');
export const MapPin = make(d_location_on, 'MapPin');
export const Maximize2 = make(d_open_in_full, 'Maximize2');
export const Megaphone = make(d_campaign, 'Megaphone');
export const MessageCircle = make(d_chat_bubble, 'MessageCircle');
export const MessageSquare = make(d_chat, 'MessageSquare');
export const MessageSquarePlus = make(d_add_comment, 'MessageSquarePlus');
export const MessageSquareText = make(d_comment, 'MessageSquareText');
export const Mic = make(d_mic, 'Mic');
export const MicOff = make(d_mic_off, 'MicOff');
export const Minimize2 = make(d_close_fullscreen, 'Minimize2');
export const Minus = make(d_remove, 'Minus');
export const MinusCircle = make(d_do_not_disturb_on, 'MinusCircle');
export const MinusIcon = make(d_remove, 'MinusIcon');
export const Monitor = make(d_desktop_windows, 'Monitor');
export const MoreHorizontal = make(d_more_horiz, 'MoreHorizontal');
export const MoreHorizontalIcon = make(d_more_horiz, 'MoreHorizontalIcon');
export const MoreVertical = make(d_more_vert, 'MoreVertical');
export const MousePointer2 = make(d_arrow_selector_tool, 'MousePointer2');
export const Move = make(d_open_with, 'Move');
export const Navigation = make(d_navigation, 'Navigation');
export const NotebookPen = make(d_edit_note, 'NotebookPen');
export const OctagonAlert = make(d_report, 'OctagonAlert');
export const Package = make(d_inventory_2, 'Package');
export const Palette = make(d_palette, 'Palette');
export const Paperclip = make(d_attach_file, 'Paperclip');
export const Pause = make(d_pause, 'Pause');
export const PauseCircle = make(d_pause_circle, 'PauseCircle');
export const Pen = make(d_edit, 'Pen');
export const PenLine = make(d_edit, 'PenLine');
export const PenSquare = make(d_edit_square, 'PenSquare');
export const Pencil = make(d_edit, 'Pencil');
export const Percent = make(d_percent, 'Percent');
export const Phone = make(d_call, 'Phone');
export const PhoneCall = make(d_phone_in_talk, 'PhoneCall');
export const PhoneOff = make(d_call_end, 'PhoneOff');
export const PictureInPicture2 = make(d_picture_in_picture, 'PictureInPicture2');
export const PieChart = make(d_pie_chart, 'PieChart');
export const Pin = make(d_push_pin, 'Pin');
export const PinOff = make(d_keep_off, 'PinOff');
export const Play = make(d_play_arrow, 'Play');
export const PlayCircle = make(d_play_circle, 'PlayCircle');
export const Plus = make(d_add, 'Plus');
export const PoundSterling = make(d_currency_pound, 'PoundSterling');
export const Presentation = make(d_co_present, 'Presentation');
export const Printer = make(d_print, 'Printer');
export const QrCode = make(d_qr_code, 'QrCode');
export const Receipt = make(d_receipt, 'Receipt');
export const ReceiptText = make(d_receipt_long, 'ReceiptText');
export const Redo2 = make(d_redo, 'Redo2');
export const RefreshCw = make(d_refresh, 'RefreshCw');
export const Repeat = make(d_repeat, 'Repeat');
export const RepeatIcon = make(d_repeat, 'RepeatIcon');
export const Reply = make(d_reply, 'Reply');
export const ReplyAll = make(d_reply_all, 'ReplyAll');
export const Rocket = make(d_rocket_launch, 'Rocket');
export const RotateCcw = make(d_rotate_left, 'RotateCcw');
export const Ruler = make(d_straighten, 'Ruler');
export const Save = make(d_save, 'Save');
export const Scale = make(d_balance, 'Scale');
export const Search = make(d_search, 'Search');
export const SearchIcon = make(d_search, 'SearchIcon');
export const SearchX = make(d_search_off, 'SearchX');
export const Send = make(d_send, 'Send');
export const SendHorizonal = make(d_send, 'SendHorizonal');
export const Server = make(d_dns, 'Server');
export const ServerCog = make(d_storage, 'ServerCog');
export const Settings = make(d_settings, 'Settings');
export const Settings2 = make(d_tune, 'Settings2');
export const Share = make(d_share, 'Share');
export const Share2 = make(d_share, 'Share2');
export const Sheet = make(d_table_chart, 'Sheet');
export const Shield = make(d_shield, 'Shield');
export const ShieldAlert = make(d_gpp_maybe, 'ShieldAlert');
export const ShieldCheck = make(d_verified_user, 'ShieldCheck');
export const ShieldOff = make(d_gpp_bad, 'ShieldOff');
export const ShieldX = make(d_gpp_bad, 'ShieldX');
export const ShoppingCart = make(d_shopping_cart, 'ShoppingCart');
export const SlidersHorizontal = make(d_tune, 'SlidersHorizontal');
export const Smartphone = make(d_smartphone, 'Smartphone');
export const SortAsc = make(d_sort, 'SortAsc');
export const Sparkles = make(d_auto_awesome, 'Sparkles');
export const Square = make(d_crop_square, 'Square');
export const SquarePen = make(d_edit_square, 'SquarePen');
export const Star = make(d_star, 'Star');
export const StickyNote = make(d_sticky_note_2, 'StickyNote');
export const Store = make(d_storefront, 'Store');
export const Sun = make(d_light_mode, 'Sun');
export const SunDim = make(d_brightness_low, 'SunDim');
export const Table = make(d_table_chart, 'Table');
export const TableProperties = make(d_table_rows, 'TableProperties');
export const Target = make(d_target, 'Target');
export const ThumbsDown = make(d_thumb_down, 'ThumbsDown');
export const ThumbsUp = make(d_thumb_up, 'ThumbsUp');
export const Timer = make(d_timer, 'Timer');
export const Trash2 = make(d_delete, 'Trash2');
export const TrendingDown = make(d_trending_down, 'TrendingDown');
export const TrendingUp = make(d_trending_up, 'TrendingUp');
export const TriangleAlert = make(d_warning, 'TriangleAlert');
export const Truck = make(d_local_shipping, 'Truck');
export const Type = make(d_text_fields, 'Type');
export const Undo2 = make(d_undo, 'Undo2');
export const Unlink = make(d_link_off, 'Unlink');
export const Unlock = make(d_lock_open, 'Unlock');
export const Upload = make(d_upload, 'Upload');
export const User = make(d_person, 'User');
export const UserCheck = make(d_how_to_reg, 'UserCheck');
export const UserCircle = make(d_account_circle, 'UserCircle');
export const UserCog = make(d_manage_accounts, 'UserCog');
export const UserMinus = make(d_person_remove, 'UserMinus');
export const UserPlus = make(d_person_add, 'UserPlus');
export const UserX = make(d_person_off, 'UserX');
export const Users = make(d_group, 'Users');
export const Users2 = make(d_groups, 'Users2');
export const UsersIcon = make(d_group, 'UsersIcon');
export const Video = make(d_videocam, 'Video');
export const VideoOff = make('M9.56 8l-2-2-4.15-4.14L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.55-.18L19.73 21l1.41-1.41-8.86-8.86L9.56 8zM5 16V8h1.73l8 8H5zm10-8v2.61l6 6V6.5l-4 4V7c0-.55-.45-1-1-1h-5.61l2 2H15z', 'VideoOff', '0 0 24 24');
export const Headphones = make('M240-120H160q-33 0-56.5-23.5T80-200v-320q0-166 117-283t283-117q166 0 283 117t117 283v320q0 33-23.5 56.5T800-120H720q-33 0-56.5-23.5T640-200v-200q0-33 23.5-56.5T720-480h80v-40q0-133-93.5-226.5T480-840q-133 0-226.5 93.5T160-520v40h80q33 0 56.5 23.5T320-400v200q0 33-23.5 56.5T240-120ZM160-200h80v-200h-80v200Zm560 0h80v-200h-80v200Z', 'Headphones');
export const Volume2 = make(d_volume_up, 'Volume2');
export const VolumeX = make(d_volume_off, 'VolumeX');
export const Wallet = make(d_account_balance_wallet, 'Wallet');
export const WalletCards = make(d_wallet, 'WalletCards');
export const Wand2 = make(d_auto_fix_high, 'Wand2');
export const WandSparkles = make(d_auto_fix_high, 'WandSparkles');
export const Wifi = make(d_wifi, 'Wifi');
export const WifiOff = make(d_wifi_off, 'WifiOff');
export const Wind = make(d_air, 'Wind');
export const Workflow = make(d_account_tree, 'Workflow');
export const Wrench = make(d_build, 'Wrench');
export const X = make(d_close, 'X');
export const XCircle = make(d_cancel, 'XCircle');
export const XIcon = make(d_close, 'XIcon');
export const Zap = make(d_bolt, 'Zap');
export const ZoomIn = make(d_zoom_in, 'ZoomIn');
export const ZoomOut = make(d_zoom_out, 'ZoomOut');

export const ArrowRange = make(d_arrow_range, 'ArrowRange');
export const DeployedCode = make(d_deployed_code, 'DeployedCode');
export const FormatColorFill = make(d_format_color_fill, 'FormatColorFill');
export const FrameInspect = make(d_frame_inspect, 'FrameInspect');
export const HighlightAlt = make(d_highlight_alt, 'HighlightAlt');
export const PanTool = make(d_pan_tool, 'PanTool');
export const Polyline = make(d_polyline, 'Polyline');
export const ScatterPlot = make(d_scatter_plot, 'ScatterPlot');
export const SquareFoot = make(d_square_foot, 'SquareFoot');
export const ViewInAr = make(d_view_in_ar, 'ViewInAr');

export const DocumentTakeoff = make(d_document_takeoff, 'DocumentTakeoff', '0 0 24 24');

export const Plug = make('M320-880h80v200h160v-200h80v200h120v160q0 106-70 184t-170 94v162h-80v-162q-100-16-170-94t-70-184v-160h120Zm-40 280v80q0 83 58.5 141.5T480-320q83 0 141.5-58.5T680-520v-80H280Z', 'Plug');

// ── filled counterparts (FILL 1) for the main rail's hover and focus state ──
// The same Material Symbols glyphs at FILL 1: same grid, weight and bounding box
// as the outlines above, so swapping one for the other never shifts a pixel.

export const HomeFilled = make(d_home_fill, 'HomeFilled');
export const CalendarDaysFilled = make(d_calendar_month_fill, 'CalendarDaysFilled');
export const MessageSquareFilled = make(d_chat_fill, 'MessageSquareFilled');
export const MailFilled = make(d_mail_fill, 'MailFilled');
export const BookUserFilled = make(d_contact_page_fill, 'BookUserFilled');
export const CreditCardFilled = make(d_credit_card_fill, 'CreditCardFilled');
export const SendFilled = make(d_send_fill, 'SendFilled');
export const KanbanSquareFilled = make(d_view_kanban_fill, 'KanbanSquareFilled');
export const BanknoteFilled = make(d_payments_fill, 'BanknoteFilled');
export const ShoppingCartFilled = make(d_shopping_cart_fill, 'ShoppingCartFilled');
export const WalletFilled = make(d_account_balance_wallet_fill, 'WalletFilled');
export const BookOpenFilled = make(d_menu_book_fill, 'BookOpenFilled');
export const LibraryBooksFilled = make(d_library_books_fill, 'LibraryBooksFilled');
export const ClipboardCheckFilled = make(d_assignment_turned_in_fill, 'ClipboardCheckFilled');
export const NotebookPenFilled = make(d_edit_note_fill, 'NotebookPenFilled');
export const TruckFilled = make(d_local_shipping_fill, 'TruckFilled');
export const BriefcaseFilled = make(d_work_fill, 'BriefcaseFilled');

/** Outline -> filled pairs. Building2 (domain) and Percent are already solid
 *  in Material and have no separate filled form, so they pair with themselves.
 *  `globalThis.Map` because this module exports a `Map` icon that shadows it. */
const FILLED_VARIANTS = new globalThis.Map<LucideIcon, LucideIcon>([
  [Home, HomeFilled],
  [CalendarDays, CalendarDaysFilled],
  [MessageSquare, MessageSquareFilled],
  [Mail, MailFilled],
  [BookUser, BookUserFilled],
  [CreditCard, CreditCardFilled],
  [Send, SendFilled],
  [KanbanSquare, KanbanSquareFilled],
  [Banknote, BanknoteFilled],
  [ShoppingCart, ShoppingCartFilled],
  [Wallet, WalletFilled],
  [BookOpen, BookOpenFilled],
  [LibraryBooks, LibraryBooksFilled],
  [ClipboardCheck, ClipboardCheckFilled],
  [NotebookPen, NotebookPenFilled],
  [Truck, TruckFilled],
  [Briefcase, BriefcaseFilled],
  [Building2, Building2],
  [Percent, Percent],
]);

/** The filled form of an outline icon, or undefined when the set has none. */
export function filledIconFor(icon: LucideIcon): LucideIcon | undefined {
  return FILLED_VARIANTS.get(icon);
}
